(() => {
  const data = window.TPLPromptGeneratorData;
  if (!data || !data.pools) return;

  const randomIndex = length => Math.floor(Math.random() * length);
  const escapeHTML = value => String(value || '').replace(/[&<>'"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));

  const pick = (items, recent = [], limit = 6, keyFn = item => item.name || item.setting || item.subject || item.format || item) => {
    if (!Array.isArray(items) || !items.length) return null;
    const eligible = items.filter(item => !recent.includes(keyFn(item)));
    const pool = eligible.length ? eligible : items;
    const item = pool[randomIndex(pool.length)];
    const key = keyFn(item);
    recent.unshift(key);
    if (recent.length > limit) recent.length = limit;
    return item;
  };

  const pickSimple = items => items[randomIndex(items.length)];
  const sample = (items, min = 0, max = 2) => {
    const copy = [...items];
    const count = Math.min(copy.length, min + randomIndex(max - min + 1));
    const result = [];
    while (result.length < count && copy.length) result.push(copy.splice(randomIndex(copy.length), 1)[0]);
    return result;
  };

  const joinList = items => {
    if (!items.length) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
  };

  const phrase = item => item.article ? `${item.article} ${item.name}` : item.name;
  const plural = item => item.plural || `${item.name}s`;
  const articleFor = value => {
    const text = String(value || '').trim();
    if (!text) return '';
    if (/^(a|an|the|some|\d+)\s+/i.test(text)) return text;
    const first = text.split(/\s+/)[0].toLowerCase();
    const useAn = /^[aeiou]/.test(first) && !/^(uni|use|user|one|once)\b/.test(first);
    return `${useAn ? 'an' : 'a'} ${text}`;
  };

  const someNouns = new Set([
    'scissors',
    'binoculars',
    'glasses',
    'reading glasses',
    'garden gloves',
    'hiking boots',
    'pruning shears',
    'pancakes',
    'registration marks'
  ]);

  const propPhraseFor = value => {
    const text = String(value || '').trim();
    if (!text) return '';
    if (/^(a|an|the|some|\d+)\s+/i.test(text)) return text;
    const lower = text.toLowerCase();
    const words = lower.split(/\s+/);
    const last = words[words.length - 1];
    if (someNouns.has(lower) || someNouns.has(last)) return `some ${text}`;
    return articleFor(text);
  };

  const joinPhraseList = items => joinList((items || []).map(propPhraseFor).filter(Boolean));
  const articleOnly = value => articleFor(value).replace(String(value || '').trim(), '');
  const patternShape = pattern => String(pattern || '').replace(/ pattern$/, ' shape').replace(/^tiny /, 'tiny ');

  const icon = name => {
    if (name === 'lock') return '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="10" width="14" height="10" rx="2"></rect><path d="M8 10V7a4 4 0 0 1 8 0v3"></path></svg>';
    if (name === 'refresh') return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12a9 9 0 0 1 15-6.7"></path><path d="M18 3v6h-6"></path><path d="M21 12a9 9 0 0 1-15 6.7"></path><path d="M6 21v-6h6"></path></svg>';
    if (name === 'copy') return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 8h11v11H8z"></path><path d="M5 16H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1"></path></svg>';
    if (name === 'sparkle') return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z"></path><path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15Z"></path></svg>';
    return '';
  };

  const getContrastColor = hex => {
    const clean = String(hex || '').replace('#', '');
    if (clean.length !== 6) return '#2B2B2B';
    const r = parseInt(clean.slice(0, 2), 16);
    const g = parseInt(clean.slice(2, 4), 16);
    const b = parseInt(clean.slice(4, 6), 16);
    return ((r * 299 + g * 587 + b * 114) / 1000) >= 150 ? '#2B2B2B' : '#FFFFFF';
  };

  const makeResult = (segments, chips, palette, lockData = {}) => ({
    segments,
    baseSegments: segments,
    twists: [],
    prompt: segments.map(part => part.text).join(''),
    chips,
    palette,
    lockData
  });

  const generatorFactory = (state, locks = {}) => {
    const recent = state.recentPieces;
    const palette = locks.paletteObject || pick(data.palettes, state.recentPalette, 10, item => item.name);
    const pickPool = (key, poolName, keyFn) => {
      if (locks[key]) return locks[key];
      return pick(data.pools[poolName], recent[poolName] || (recent[poolName] = []), 8, keyFn);
    };
    const pickValue = (key, values) => locks[key] || pickSimple(values);
    return { palette, pickPool, pickValue, locks };
  };


  const chooseCharacterRole = creature => {
    const exclusions = new Set((data.pools.characterExclusions || []).map(item => `${item.creature}|${item.role}`));
    const roles = (data.pools.characterRoles || []).filter(role => !exclusions.has(`${creature}|${role}`));
    return roles.length ? pickSimple(roles) : pickSimple(data.pools.characterRoles || []);
  };

  const chooseCharacterPair = (locks = {}) => {
    const lockedCreature = locks.characterCreature;
    const lockedRole = locks.characterRole;
    if (lockedCreature && lockedRole) return { creature: lockedCreature, role: lockedRole };
    if (lockedCreature) return { creature: lockedCreature, role: chooseCharacterRole(lockedCreature) };
    if (lockedRole) {
      const exclusions = new Set((data.pools.characterExclusions || []).map(item => `${item.creature}|${item.role}`));
      const creatures = (data.pools.characterCreatures || []).filter(creature => !exclusions.has(`${creature}|${lockedRole}`));
      return { creature: creatures.length ? pickSimple(creatures) : pickSimple(data.pools.characterCreatures || []), role: lockedRole };
    }
    if (Math.random() < 0.5 && Array.isArray(data.pools.characterPreferredPairings) && data.pools.characterPreferredPairings.length) {
      const pairing = pickSimple(data.pools.characterPreferredPairings);
      return { creature: pairing.creature, role: pairing.role };
    }
    const creature = pickSimple(data.pools.characterCreatures || []);
    return { creature, role: chooseCharacterRole(creature) };
  };


const generateQuick = ctx => {
  const subject = ctx.pickPool('subjectObject', 'quickObjects', item => item.name);
  const action = pickSimple(['Draw', 'Illustrate', 'Sketch', 'Paint', 'Make']);
  const palette = ctx.palette;
  const shortChallenges = [
    'Use the lightest palette color on the largest shape',
    'Save the darkest palette color for the smallest details',
    'Repeat one shape exactly three times',
    'Let one color show up only once',
    'Make one small detail the focal point'
  ];
  const challenge = ctx.pickValue('challenge', shortChallenges);
  const useChallenge = Math.random() < 0.45;

  if ((subject.name === 'mushroom' || subject.name === 'flower') && Math.random() < 0.28) {
    const count = 3;
    const detail = subject.name === 'mushroom' ? 'cap shapes' : 'petal shapes';
    const challengeLine = subject.name === 'mushroom' ? 'Vary the cap shapes' : 'Make one flower the focal point';
    const segments = [
      { text: `${action} ${count} ` },
      { text: plural(subject), key: 'subject' },
      { text: `. ` },
      { text: `${challengeLine}.`, key: 'challenge' }
    ];
    return makeResult(segments, { format: 'quick set', subject: `${count} ${plural(subject)}`, challenge: challengeLine }, palette, {
      subject: { subjectObject: subject },
      challenge: { challenge: challengeLine },
      palette: { paletteObject: palette }
    });
  }

  const segments = [
    { text: `${action} ` },
    { text: phrase(subject), key: 'subject' },
    { text: `.` }
  ];

  if (useChallenge) {
    segments.push(
      { text: ` ` },
      { text: `${challenge}.`, key: 'challenge' }
    );
  }

  const chips = { format: 'quick drawing', subject: subject.name };
  const lockData = {
    subject: { subjectObject: subject },
    palette: { paletteObject: palette }
  };

  if (useChallenge) {
    chips.challenge = challenge;
    lockData.challenge = { challenge };
  }

  return makeResult(segments, chips, palette, lockData);
};

const generateFull = ctx => {
    const item = ctx.pickPool('fullFormatObject', 'fullFormats', entry => `${entry.format}-${entry.subject}`);
    const action = item.format.includes('map') || item.format.includes('poster') || item.format.includes('cover') ? 'Design' : pickSimple(['Create', 'Design', 'Make']);
    const pieces = ctx.locks.detailPieces || sample(item.pieces, 2, 3);
    const segments = [
      { text: `${action} a ` },
      { text: item.format, key: 'format' },
      { text: ` for ` },
      { text: item.subject, key: 'subject' },
      { text: `. Include ` },
      { text: joinList(pieces), key: 'detail' },
      { text: `.` }
    ];
    return makeResult(segments, { format: item.format, subject: item.subject, detail: joinList(pieces) }, ctx.palette, {
      format: { fullFormatObject: item },
      subject: { fullFormatObject: item },
      detail: { detailPieces: pieces },
      palette: { paletteObject: ctx.palette }
    });
  };

  const generateCozy = ctx => {
    const scene = ctx.pickPool('cozySceneObject', 'cozyScenes', item => item.setting);
    const action = pickSimple(['Draw', 'Illustrate', 'Create']);
    const addOns = ctx.locks.detailAddOns || sample(scene.addOns, 1, 2);
    const challenge = ctx.pickValue('challenge', scene.constraints);
    const segments = [
      { text: `${action} a ` },
      { text: scene.setting, key: 'subject' },
      { text: addOns.length ? ` with ${joinList(addOns)}` : '' , key: addOns.length ? 'detail' : undefined },
      { text: `. ` },
      { text: challenge, key: 'challenge' }
    ];
    return makeResult(segments, { format: 'cozy scene', subject: scene.setting, detail: joinList(addOns), challenge }, ctx.palette, {
      subject: { cozySceneObject: scene },
      detail: { detailAddOns: addOns },
      challenge: { challenge },
      palette: { paletteObject: ctx.palette }
    });
  };


const generateWeird = ctx => {
  const weirdType = ctx.locks.weirdType || pickSimple(['object', 'creature', 'pattern']);

  if (weirdType === 'creature') {
    const creature = ctx.locks.weirdCreature || ctx.pickPool('weirdCreature', 'weirdCreatures', item => item);
    const condition = ctx.locks.weirdCondition || ctx.pickPool('weirdCondition', 'weirdCreatureConditions', item => item);
    const segments = [
      { text: 'Draw ' },
      { text: articleFor(creature), key: 'subject' },
      { text: ' ' },
      { text: condition, key: 'detail' },
      { text: '.' }
    ];
    return makeResult(segments, { format: 'weird creature', subject: creature, detail: condition }, ctx.palette, {
      subject: { weirdType: 'creature', weirdCreature: creature },
      detail: { weirdType: 'creature', weirdCondition: condition },
      palette: { paletteObject: ctx.palette }
    });
  }

  if (weirdType === 'pattern') {
    const phraseItem = ctx.locks.weirdPattern || ctx.pickPool('weirdPattern', 'weirdPatternPrompts', item => item);
    const segments = [
      { text: 'Create a motif set of ' },
      { text: phraseItem, key: 'subject' },
      { text: '.' }
    ];
    return makeResult(segments, { format: 'weird pattern', subject: phraseItem }, ctx.palette, {
      subject: { weirdType: 'pattern', weirdPattern: phraseItem },
      palette: { paletteObject: ctx.palette }
    });
  }

  const transformation = ctx.locks.weirdObject || ctx.pickPool('weirdObject', 'weirdObjectTransformations', item => item);
  const action = pickSimple(['Draw', 'Illustrate', 'Design']);
  const segments = [
    { text: `${action} ` },
    { text: articleFor(transformation), key: 'subject' },
    { text: '.' }
  ];
  return makeResult(segments, { format: 'weird object', subject: transformation }, ctx.palette, {
    subject: { weirdType: 'object', weirdObject: transformation },
    palette: { paletteObject: ctx.palette }
  });
};

const generatePattern = ctx => {
    const item = ctx.pickPool('patternPromptObject', 'patternPrompts', entry => `${entry.format}-${entry.motifs.join('-')}`);
    const segments = [
      { text: `${item.action} a ` },
      { text: item.format, key: 'format' },
      { text: ` using ` },
      { text: joinList(item.motifs), key: 'subject' },
      { text: `. ` },
      { text: item.constraint, key: 'challenge' }
    ];
    return makeResult(segments, { format: item.format, subject: joinList(item.motifs), challenge: item.constraint }, ctx.palette, {
      format: { patternPromptObject: item },
      subject: { patternPromptObject: item },
      challenge: { patternPromptObject: item },
      palette: { paletteObject: ctx.palette }
    });
  };


const generateCharacter = ctx => {
  const pair = chooseCharacterPair(ctx.locks || {});
  const creature = pair.creature;
  const role = pair.role;
  const propsPool = (data.pools.characterRoleProps && data.pools.characterRoleProps[role]) || [];
  const props = ctx.locks.detailProps || sample(propsPool, 1, 2);
  const action = pickSimple(['Draw', 'Illustrate', 'Design', 'Create']);
  const subjectPhrase = `${creature} ${role}`;
  const propText = joinPhraseList(props);
  const segments = [
    { text: `${action} ` },
    { text: articleFor(subjectPhrase), key: 'subject' },
    { text: propText ? ` with ${propText}` : '', key: propText ? 'detail' : undefined },
    { text: '.' }
  ];
  return makeResult(segments, { format: 'character design', subject: subjectPhrase, detail: propText }, ctx.palette, {
    subject: { characterCreature: creature, characterRole: role },
    detail: { detailProps: props },
    palette: { paletteObject: ctx.palette }
  });
};

const generators = {
    quick: generateQuick,
    full: generateFull,
    cozy: generateCozy,
    weird: generateWeird,
    pattern: generatePattern,
    character: generateCharacter
  };

  const modeLabels = {
    quick: 'Quick',
    full: 'Full chaos',
    cozy: 'Cozy',
    weird: 'Weird',
    pattern: 'Pattern',
    character: 'Character'
  };

  const modeKeys = Object.keys(generators);

  const init = section => {
    if (!section || section.dataset.tplPromptReady === 'true') return;
    section.dataset.tplPromptReady = 'true';

    const configEl = section.querySelector('[data-prompt-config]');
    const config = configEl ? JSON.parse(configEl.textContent) : {};
    const parsedCooldownSeconds = Number(config.cooldownSeconds);
    const cooldownSeconds = Number.isFinite(parsedCooldownSeconds) && parsedCooldownSeconds > 0 ? Math.round(parsedCooldownSeconds) : 20;
    const cooldownEnabled = Boolean(config.cooldownEnabled);
    const labels = config.labels || {};
    const generatePromptLabel = !labels.generate || labels.generate === 'Give me my assignment' ? 'Generate prompt' : labels.generate;
    const emptyPromptMessage = !labels.empty || labels.empty === 'Ready when you are. Hit generate to begin.' ? 'Pick a mode above, then press Generate.\nYour first assignment is waiting impatiently.' : labels.empty;
    section.dataset.cooldownSeconds = String(cooldownSeconds);

    const state = {
      mode: config.defaultMode || 'quick',
      current: null,
      locked: {},
      recentPalette: [],
      recentPieces: {},
      cooldownTimer: null,
      cooldownRemaining: 0,
      thinkingKey: null,
      currentTwist: null,
      fineTuneTimer: null,
      hasGeneratedPromptBefore: false
    };

    const els = {
      modes: [...section.querySelectorAll('[data-prompt-mode]')],
      sentence: section.querySelector('[data-prompt-sentence]'),
      palette: section.querySelector('[data-prompt-palette-swatches]'),
      chips: section.querySelector('[data-prompt-chips]'),
      fineTune: section.querySelector('.tpl-prompt-generator__fine-tune'),
      generate: section.querySelector('[data-generate-prompt]'),
      copy: section.querySelector('[data-copy-prompt]'),
      twist: section.querySelector('[data-add-twist]'),
      notice: section.querySelector('[data-prompt-notice]'),
      modeNotice: section.querySelector('[data-mode-countdown-note]'),
      promptCard: section.querySelector('.tpl-prompt-generator__card')
    };

    const setNotice = (message, duration = 2800) => {
      if (!els.notice) return;
      els.notice.textContent = message || '';
      if (message) window.setTimeout(() => {
        if (els.notice.textContent === message) els.notice.textContent = '';
      }, duration);
    };

    const setCooldownNotes = value => {
      const buttonNote = value > 0 ? (labels.idle || '') : '';
      const modeNote = value > 0 ? (labels.modeCountdown || '') : '';
      if (els.notice) els.notice.textContent = buttonNote;
      if (els.modeNotice) els.modeNotice.textContent = modeNote;
    };

    const updateLaunchCues = () => {
      const isPrelaunch = !state.current;
      section.classList.toggle('is-prelaunch', isPrelaunch);
      if (els.generate) els.generate.classList.toggle('is-first-session', isPrelaunch);
    };

    const hideFineTune = () => {
      if (!els.fineTune) return;
      els.fineTune.classList.add('is-hidden');
      els.fineTune.classList.remove('is-intro');
    };

    const revealFineTune = delayed => {
      if (!els.fineTune) return;
      window.clearTimeout(state.fineTuneTimer);
      if (delayed) {
        els.fineTune.classList.add('is-hidden', 'is-intro');
        state.fineTuneTimer = window.setTimeout(() => {
          section.classList.remove('is-prelaunch');
          els.fineTune.classList.remove('is-hidden');
          window.requestAnimationFrame(() => {
            els.fineTune.classList.remove('is-intro');
          });
        }, 520);
      } else {
        section.classList.remove('is-prelaunch');
        els.fineTune.classList.remove('is-hidden', 'is-intro');
      }
    };

    const cooldownStorageKeyBase = `tplPromptCooldown:${section.id || 'default'}`;
    const cooldownStorageKey = `${cooldownStorageKeyBase}:${cooldownSeconds}`;

    try {
      // Clear every old prompt-generator cooldown key so stale 30s timers cannot
      // survive section reloads, theme-editor preview IDs, or changing the editor value.
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('tplPromptCooldown:') && key !== cooldownStorageKey) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (error) {}

    const setCooldownEnd = seconds => {
      try {
        if (seconds > 0) sessionStorage.setItem(cooldownStorageKey, String(Date.now() + seconds * 1000));
        else sessionStorage.removeItem(cooldownStorageKey);
      } catch (error) {}
    };

    const getCooldownRemaining = () => {
      try {
        const end = Number(sessionStorage.getItem(cooldownStorageKey) || 0);
        if (!end) return 0;
        const remaining = Math.ceil((end - Date.now()) / 1000);
        if (remaining <= 0) {
          sessionStorage.removeItem(cooldownStorageKey);
          return 0;
        }
        return remaining;
      } catch (error) {
        return 0;
      }
    };

    const cacheModeIcons = () => {
      els.modes.forEach(button => {
        const iconEl = button.querySelector('.tpl-prompt-generator__mode-icon');
        if (iconEl && !iconEl.dataset.originalIcon) iconEl.dataset.originalIcon = iconEl.innerHTML;
      });
    };

    const updateModeCountdownIcons = value => {
      els.modes.forEach(button => {
        const iconEl = button.querySelector('.tpl-prompt-generator__mode-icon');
        if (!iconEl) return;
        if (value > 0) {
          iconEl.classList.add('is-countdown');
          iconEl.textContent = value;
        } else {
          iconEl.classList.remove('is-countdown');
          if (iconEl.dataset.originalIcon) iconEl.innerHTML = iconEl.dataset.originalIcon;
        }
      });
    };

    const currentTwistColor = () => {
      const hex = midTonePaletteColor();
      return { hex, text: hex ? getContrastColor(hex) : '' };
    };

    const colorLuminance = hex => {
      const clean = String(hex || '').replace('#', '');
      if (clean.length !== 6) return 0.5;
      const r = parseInt(clean.slice(0, 2), 16) / 255;
      const g = parseInt(clean.slice(2, 4), 16) / 255;
      const b = parseInt(clean.slice(4, 6), 16) / 255;
      const convert = value => value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
      return 0.2126 * convert(r) + 0.7152 * convert(g) + 0.0722 * convert(b);
    };

    const midTonePaletteColor = () => {
      const colors = state.current && state.current.palette && state.current.palette.colors ? state.current.palette.colors : [];
      if (!colors.length) return '';
      const sorted = [...colors].sort((a, b) => Math.abs(colorLuminance(a.hex) - 0.45) - Math.abs(colorLuminance(b.hex) - 0.45));
      return sorted[0] && sorted[0].hex ? sorted[0].hex : '';
    };

    const shufflePulseColor = () => midTonePaletteColor();

    const renderSegments = (segments, animate = false, updatedKey = null) => segments.map((part, index) => {
      const text = escapeHTML(part.text);
      const updated = updatedKey && part.key === updatedKey ? ' is-updated' : '';

      if (part.key === 'twistBlock') {
        const color = part.color || {};
        const style = color.hex ? ` style="--tpl-current-twist-color:${escapeHTML(color.hex)};--tpl-current-twist-text:${escapeHTML(color.text || getContrastColor(color.hex))}"` : '';
        return `<span class="tpl-prompt-generator__twist-line"${style}><span class="tpl-prompt-generator__twist-label">Twist: </span><span class="tpl-prompt-generator__highlight tpl-prompt-generator__highlight--twist${updated}" data-highlight="twist">${escapeHTML(part.text)}</span></span>`;
      }

      if (part.isBreak) return '';
      if (!part.key) return text;

      const thinking = part.key === state.thinkingKey ? ' is-thinking' : '';
      return `<span class="tpl-prompt-generator__highlight tpl-prompt-generator__highlight--plain${updated}${thinking}" data-highlight="${escapeHTML(part.key)}">${text}</span>`;
    }).join('');

    const renderPalette = (palette, animate = true) => {
      if (!els.palette || !palette) return;
      els.palette.innerHTML = palette.colors.map((color, index) => {
        const contrast = getContrastColor(color.hex);
        const staticClass = animate ? '' : ' is-static';
        const delay = animate ? `${760 + index * 100}ms` : '0ms';
        return `<span class="tpl-prompt-palette__swatch${staticClass}" style="--tpl-swatch-color:${escapeHTML(color.hex)};--tpl-swatch-text:${contrast};--tpl-swatch-delay:${delay}"><span class="tpl-prompt-palette__blob"><span>${escapeHTML(color.name)}</span></span></span>`;
      }).join('');
    };

    const chipHTML = (key, value, index, animate, updatedKey) => {
      const label = key === 'mode' || key === 'format' ? 'Mode' : key.replace(/([A-Z])/g, ' $1').replace(/^./, char => char.toUpperCase());
      const locked = Boolean(state.locked[key]);
      const staticClass = animate ? '' : ' is-static';
      const updatedClass = updatedKey === key ? ' is-updated' : '';
      const thinkingClass = state.thinkingKey === key ? ' is-thinking' : '';
      const delay = animate ? `${index * 90}ms` : '0ms';
      return `<div class="tpl-prompt-chip${staticClass}${updatedClass}${thinkingClass}" style="--tpl-chip-delay:${delay}" data-chip="${escapeHTML(key)}">
        <span class="tpl-prompt-chip__copy"><span class="tpl-prompt-chip__label">${escapeHTML(label)}</span><span class="tpl-prompt-chip__value">${escapeHTML(value)}</span></span>
        <span class="tpl-prompt-chip__actions">
          <button type="button" class="tpl-prompt-chip__button" data-lock-chip="${escapeHTML(key)}" aria-label="${locked ? 'Unlock' : 'Lock'} ${escapeHTML(label)}" aria-pressed="${locked ? 'true' : 'false'}">${icon('lock')}</button>
          <button type="button" class="tpl-prompt-chip__button" data-reroll-chip="${escapeHTML(key)}" aria-label="Reroll ${escapeHTML(label)}">${icon('refresh')}</button>
        </span>
      </div>`;
    };

    const chipEntriesFor = item => {
      const chipEntries = Object.entries(item.chips || {}).filter(([key, value]) => key !== 'twist' && value);
      const paletteEntry = ['palette', item.palette ? item.palette.name : ''];
      return [...chipEntries, paletteEntry];
    };

    const renderChips = (item, animate = true, updatedKey = null) => {
      if (!els.chips) return;
      const allEntries = chipEntriesFor(item);

      if (!animate && els.chips.children.length) {
        allEntries.forEach(([key, value], index) => {
          let chip = els.chips.querySelector(`[data-chip="${CSS.escape(key)}"]`);
          if (!chip) {
            els.chips.insertAdjacentHTML('beforeend', chipHTML(key, value, index, false, updatedKey));
            chip = els.chips.querySelector(`[data-chip="${CSS.escape(key)}"]`);
          }

          chip.classList.add('is-static');
          chip.classList.remove('is-thinking', 'is-updated');
          if (updatedKey === key) chip.classList.add('is-updated');

          const valueEl = chip.querySelector('.tpl-prompt-chip__value');
          if (valueEl) {
            valueEl.classList.remove('is-shuffling');
            if (valueEl.textContent !== String(value)) valueEl.textContent = value;
          }

          const lockButton = chip.querySelector('[data-lock-chip]');
          const locked = Boolean(state.locked[key]);
          const label = key === 'mode' || key === 'format' ? 'Mode' : key.replace(/([A-Z])/g, ' $1').replace(/^./, char => char.toUpperCase());
          if (lockButton) {
            lockButton.setAttribute('aria-pressed', locked ? 'true' : 'false');
            lockButton.setAttribute('aria-label', `${locked ? 'Unlock' : 'Lock'} ${label}`);
          }
        });

        [...els.chips.querySelectorAll('[data-chip]')].forEach(chip => {
          const key = chip.dataset.chip;
          if (!allEntries.some(([entryKey]) => entryKey === key)) chip.remove();
        });

        return;
      }

      els.chips.innerHTML = allEntries.map(([key, value], index) => chipHTML(key, value, index, animate, updatedKey)).join('');
    };

    const renderPrompt = (item, options = {}) => {
      const animate = options.animate !== false;
      const updatedKey = options.updatedKey || null;
      const paletteChanged = !state.current || !state.current.palette || !item.palette || state.current.palette.name !== item.palette.name;
      state.thinkingKey = null;
      state.current = item;
      renderChips(item, animate, updatedKey);
      renderPalette(item.palette, animate || paletteChanged);
      if (els.sentence) {
        const revealColor = midTonePaletteColor();
        if (revealColor) els.sentence.style.setProperty('--tpl-shuffle-color', revealColor);
        els.sentence.classList.remove('is-empty', 'is-revealed', 'is-slotting', 'is-quiet-update');
        if (els.promptCard) els.promptCard.classList.remove('is-empty');
        if (els.palette) els.palette.closest('.tpl-prompt-palette')?.classList.remove('is-empty');
        els.sentence.innerHTML = renderSegments(item.segments, animate, updatedKey);
        if (updatedKey) {
          const updatedEls = els.sentence.querySelectorAll('.tpl-prompt-generator__highlight.is-updated');
          updatedEls.forEach(el => el.classList.add('is-choice-reveal'));
          window.setTimeout(() => {
            updatedEls.forEach(el => el.classList.add('is-choice-settled'));
          }, 90);
          window.setTimeout(() => {
            updatedEls.forEach(el => el.classList.remove('is-updated', 'is-choice-reveal', 'is-choice-settled', 'is-settled'));
          }, 2100);
        }
        if (animate) {
          window.requestAnimationFrame(() => {
            els.sentence.classList.add('is-revealed', 'is-slotting');
            window.setTimeout(() => els.sentence.classList.remove('is-slotting'), 560);
          });
        } else {
          els.sentence.style.opacity = '1';
          els.sentence.style.transform = 'none';
          els.sentence.classList.add('is-quiet-update');
        }
      }
      const isFirstGeneratedPrompt = !state.hasGeneratedPromptBefore;
      revealFineTune(isFirstGeneratedPrompt);
      if (!isFirstGeneratedPrompt) updateLaunchCues();
      state.hasGeneratedPromptBefore = true;
    };

    const mergeLocks = (target, source) => {
      Object.entries(source || {}).forEach(([key, value]) => {
        target[key] = value;
      });
      return target;
    };

    const locksFromState = () => {
      const locks = {};
      if (!state.current) return locks;
      Object.entries(state.locked).forEach(([key, isLocked]) => {
        if (!isLocked) return;
        if (key === 'palette') locks.paletteObject = state.current.palette;
        else mergeLocks(locks, state.current.lockData && state.current.lockData[key]);
      });
      return locks;
    };

    const generateResult = (mode, locks = {}) => {
      const generator = generators[mode] || generators.quick;
      const ctx = generatorFactory(state, locks);
      const result = generator(ctx);
      result.mode = mode;
      result.chips = {
        ...Object.fromEntries(Object.entries(result.chips || {}).filter(([key]) => key !== 'format' && key !== 'mode'))
      };
      result.lockData = {
        ...(result.lockData || {})
      };
      return result;
    };

    const finishCooldown = () => {
      window.clearInterval(state.cooldownTimer);
      state.cooldownRemaining = 0;
      setCooldownEnd(0);
      if (els.generate) {
        els.generate.disabled = false;
        els.generate.classList.remove('is-cooling-down');
        els.generate.querySelector('[data-generate-label]').textContent = generatePromptLabel;
      }
      els.modes.forEach(button => button.classList.remove('is-cooling-locked'));
      updateModeCountdownIcons(0);
      setCooldownNotes(0);
    };

    const startCooldown = (seconds = cooldownSeconds, persist = true) => {
      if (!cooldownEnabled || !els.generate) return;
      window.clearInterval(state.cooldownTimer);
      state.cooldownRemaining = Math.max(1, Number(seconds || cooldownSeconds));
      if (persist) setCooldownEnd(state.cooldownRemaining);
      els.generate.disabled = true;
      els.generate.classList.add('is-cooling-down');
      els.modes.forEach(button => button.classList.add('is-cooling-locked'));
      els.generate.querySelector('[data-generate-label]').textContent = `${labels.cooldown || 'Sit with it'} ${state.cooldownRemaining}`;
      updateModeCountdownIcons(state.cooldownRemaining);
      setCooldownNotes(state.cooldownRemaining);
      state.cooldownTimer = window.setInterval(() => {
        state.cooldownRemaining = getCooldownRemaining();
        if (state.cooldownRemaining <= 0) {
          finishCooldown();
          return;
        }
        els.generate.querySelector('[data-generate-label]').textContent = `${labels.cooldown || 'Sit with it'} ${state.cooldownRemaining}`;
        updateModeCountdownIcons(state.cooldownRemaining);
        setCooldownNotes(state.cooldownRemaining);
      }, 1000);
    };

    const resumeCooldown = () => {
      const remaining = getCooldownRemaining();
      if (remaining > 0) startCooldown(remaining, false);
      else finishCooldown();
    };

    const generatePrompt = (useCooldown = true, extraLocks = null, renderOptions = {}) => {
      const result = generateResult(state.mode, extraLocks || locksFromState());

      // Preserve twists during fine-tune/palette rerolls. A fresh Generate or mode
      // change starts a clean assignment, but small edits should not erase the twist.
      if (!useCooldown && state.current && Array.isArray(state.current.twists) && state.current.twists.length) {
        const twists = state.current.twists;
        const baseSegments = result.baseSegments || result.segments;
        result.baseSegments = baseSegments;
        result.twists = twists;
        result.segments = buildSegmentsWithTwists(baseSegments, twists);
        result.prompt = twistPromptText(baseSegments.map(part => part.text).join(''), twists);
      }

      renderPrompt(result, renderOptions);
      if (useCooldown) startCooldown();
    };

    const activateModeButton = mode => {
      els.modes.forEach(button => {
        const active = button.dataset.promptMode === mode;
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    };

    const setEmptyState = () => {
      state.current = null;
      if (els.sentence) {
        els.sentence.classList.add('is-empty');
        els.sentence.textContent = labels.empty || 'Pick a mode above, then press Generate.\nYour first assignment is waiting impatiently.';
      }
      if (els.promptCard) els.promptCard.classList.add('is-empty');
      if (els.palette) {
        els.palette.closest('.tpl-prompt-palette')?.classList.add('is-empty');
        els.palette.innerHTML = '';
      }
      if (els.chips) els.chips.innerHTML = '';
      hideFineTune();
      updateLaunchCues();
    };

    const setMode = (mode, options = {}) => {
      const isUserAction = Boolean(options.userAction);
      if (isUserAction && state.cooldownRemaining > 0) {
        setNotice(labels.cooldownMode || `Challenge rule: sit with this one for ${state.cooldownRemaining} more seconds.`, 4200);
        return;
      }

      state.mode = mode;
      state.locked = {};
      activateModeButton(mode);

      // Before the first prompt exists, mode buttons only queue the chosen mode.
      // After a prompt exists, a user-clicked mode change generates a new assignment.
      if (isUserAction && !state.current) {
        setEmptyState();
        return;
      }

      if (isUserAction) generatePrompt(true, null, { animate: true });
    };

    const showThinkingState = key => {
      state.thinkingKey = key;

      if (els.chips) {
        const chip = els.chips.querySelector(`[data-chip="${CSS.escape(key)}"]`);
        if (chip) {
          chip.classList.add('is-thinking');
          const value = chip.querySelector('.tpl-prompt-chip__value');
          if (value && key !== 'palette') {
            value.textContent = 'shuffling…';
            value.classList.add('is-shuffling');
          }
        }
      }

      if (els.sentence && key !== 'palette') {
        const pulseColor = shufflePulseColor();
        els.sentence.querySelectorAll(`[data-highlight="${CSS.escape(key)}"]`).forEach(el => {
          if (pulseColor) el.style.setProperty('--tpl-shuffle-color', pulseColor);
          el.classList.add('is-thinking', 'is-shuffling');
        });
      }
    };

    const locksForReroll = key => {
      const locks = {};
      const currentLockData = state.current && state.current.lockData ? state.current.lockData : {};

      Object.entries(currentLockData).forEach(([chipKey, lockValues]) => {
        if (chipKey === key || chipKey === 'mode' || chipKey === 'format') return;
        mergeLocks(locks, lockValues);
      });

      if (key !== 'palette' && state.current && state.current.palette) locks.paletteObject = state.current.palette;
      return locks;
    };

    const rerollChip = key => {
      if (!state.current) return;
      if (state.locked[key]) {
        setNotice(labels.locked || 'That chip is locked.', 5200);
        return;
      }

      if (key === 'palette') {
        generatePrompt(false, locksForReroll(key), { animate: false, updatedKey: null });
        return;
      }

      showThinkingState(key);
      window.setTimeout(() => {
        generatePrompt(false, locksForReroll(key), { animate: false, updatedKey: key });
      }, 780);
    };

    const buildSegmentsWithTwists = (baseSegments, twists) => [
      ...baseSegments,
      ...twists.map(twist => ({
        text: twist.text,
        key: 'twistBlock',
        color: twist.color
      }))
    ];

    const twistPromptText = (basePrompt, twists) => [
      basePrompt,
      ...twists.map(twist => `Twist: ${twist.text}`)
    ].join('\n');

    const addTwist = () => {
      if (!state.current || !data.pools.twists || !data.pools.twists.length) return;

      const existingTwists = Array.isArray(state.current.twists) ? state.current.twists : [];
      if (existingTwists.length >= 3) {
        setNotice('Twist cap reached. Three is plenty of chaos.', 4200);
        return;
      }

      const twistText = pick(data.pools.twists, state.recentPieces.twists || (state.recentPieces.twists = []), 8, item => item);
      const color = currentTwistColor();
      const twists = [
        ...existingTwists,
        { text: twistText, color }
      ];

      const baseSegments = state.current.baseSegments || state.current.segments.filter(part => part.key !== 'twistBlock' && part.key !== 'twist' && part.key !== 'twistLabel' && !part.isBreak);
      const basePrompt = baseSegments.map(part => part.text).join('');

      const base = {
        ...state.current,
        baseSegments,
        twists,
        segments: buildSegmentsWithTwists(baseSegments, twists),
        chips: {
          ...state.current.chips
        },
        lockData: {
          ...(state.current.lockData || {}),
          twist: { twists }
        }
      };

      base.prompt = twistPromptText(basePrompt, twists);
      renderPrompt(base, { animate: false, updatedKey: 'twist' });
    };

    const copyPrompt = async () => {
      if (!state.current) return;
      const paletteText = state.current.palette ? `Palette: ${state.current.palette.name}` : '';
      const text = [state.current.prompt, paletteText].filter(Boolean).join('\n');
      try {
        await navigator.clipboard.writeText(text);
        setNotice(labels.copied || 'Copied.');
      } catch (error) {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setNotice(labels.copied || 'Copied.');
      }
    };

    els.modes.forEach(button => button.addEventListener('click', () => setMode(button.dataset.promptMode, { userAction: true })));
    if (els.generate) els.generate.addEventListener('click', () => {
      // Stop the first-load wiggle immediately on the first actual press.
      els.generate.classList.remove('is-first-session');
      if (els.sentence) els.sentence.classList.add('is-slotting');
      window.setTimeout(() => generatePrompt(true), 260);
    });
    if (els.copy) els.copy.addEventListener('click', copyPrompt);
    if (els.twist) els.twist.addEventListener('click', addTwist);

    section.addEventListener('click', event => {
      const lock = event.target.closest('[data-lock-chip]');
      if (lock) {
        const key = lock.dataset.lockChip;
        state.locked[key] = !state.locked[key];
        renderChips(state.current, false);
        return;
      }
      const reroll = event.target.closest('[data-reroll-chip]');
      if (reroll) rerollChip(reroll.dataset.rerollChip);
    });

    setCooldownNotes(0);
    setNotice('');
    if (els.generate) {
      const labelEl = els.generate.querySelector('[data-generate-label]');
      if (labelEl && (!labelEl.textContent.trim() || labelEl.textContent.trim() === 'Give me my assignment')) labelEl.textContent = generatePromptLabel;
    }
    cacheModeIcons();
    activateModeButton(state.mode);
    setEmptyState();
    updateLaunchCues();
    resumeCooldown();
  };

  const boot = () => document.querySelectorAll('[data-tpl-prompt-generator]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', event => {
    const section = event.target.querySelector('[data-tpl-prompt-generator]');
    if (section) init(section);
  });
})();